var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useCallback, useMemo, useEffect, useRef, } from 'react';
import { BadgeAlt as Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Switch, Checkbox, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, useToast, } from '@cargomatic/ui';
import { ChevronDown, Loader2 } from 'lucide-react';
import { cloneDeep, groupBy } from 'lodash';
import MissingEquipmentWarning from './MissingEquipmentWarning';
import CargomaticAlert from 'components/ui/Alert';
import { generateInvoicesByGrouping, validateInvoiceChargesByGrouping, } from 'utils/AccountingWebAPIUtils';
import { useShipperInvoiceConfig } from './hooks/useShipperInvoiceConfig';
import SessionStore from 'stores/SessionStore';
import formatMoney from 'utils/formatters/Money';
import { humanize, titleize } from 'underscore.string';
const FUEL_SURCHARGE_TYPES = [
    'fuel_surcharge',
    'fuel_surcharge_bobtail',
    'fuel_surcharge_linehaul',
];
const BUCKET_TEMPLATES = {
    ONE_PER_SHIPMENT: {
        ALL: { charges: [], groupedChargeTypes: [] },
    },
    ONE_PER_SHIPMENT_PER_CHARGE_TYPE: {
        SHIPPER_COST: { charges: [], groupedChargeTypes: [] },
        FUEL_SURCHARGE: { charges: [], groupedChargeTypes: [] },
        ALL_BUT_LINE_HAUL_AND_FUEL_SURCHARGE: {
            charges: [],
            groupedChargeTypes: [],
        },
    },
    TWO_PER_SHIPMENT: {
        CHASSIS_AND_PER_DIEM: { charges: [], groupedChargeTypes: [] },
        ALL_BUT_CHASSIS_AND_PER_DIEM: { charges: [], groupedChargeTypes: [] },
    },
    UNLIMITED: {
        SHIPPER_COST: { charges: [], groupedChargeTypes: [] },
        FUEL_SURCHARGE: { charges: [], groupedChargeTypes: [] },
        ALL_BUT_LINE_HAUL_AND_FUEL_SURCHARGE: {
            charges: [],
            groupedChargeTypes: [],
        },
    },
};
const BUCKET_DISPLAY_NAMES = {
    ALL: 'All Charges',
    SHIPPER_COST: 'Shipper Cost',
    FUEL_SURCHARGE: 'Fuel Surcharge',
    ALL_BUT_LINE_HAUL_AND_FUEL_SURCHARGE: 'All but Linehaul and Fuel Surcharge',
    CHASSIS_AND_PER_DIEM: 'Chassis and Per Diem',
    ALL_BUT_CHASSIS_AND_PER_DIEM: 'All but Chassis and Per Diem',
};
const GROUPING_DISPLAY_NAMES = {
    ONE_PER_SHIPMENT: 'One Invoice per Shipment',
    ONE_PER_SHIPMENT_PER_CHARGE_TYPE: 'One Invoice per Shipment per Charge Type',
    TWO_PER_SHIPMENT: 'Two Invoices per Shipment',
    UNLIMITED: 'Unlimited',
};
function classifyCharge(charge, groupingType) {
    var _a;
    if (groupingType === 'ONE_PER_SHIPMENT') {
        return 'ALL';
    }
    if (groupingType === 'TWO_PER_SHIPMENT') {
        if (charge.type === 'chassis' || charge.type === 'per_diem') {
            return 'CHASSIS_AND_PER_DIEM';
        }
        return 'ALL_BUT_CHASSIS_AND_PER_DIEM';
    }
    if (charge.type === 'shipper_cost') {
        return 'SHIPPER_COST';
    }
    if (FUEL_SURCHARGE_TYPES.includes((_a = charge.type) !== null && _a !== void 0 ? _a : '')) {
        return 'FUEL_SURCHARGE';
    }
    return 'ALL_BUT_LINE_HAUL_AND_FUEL_SURCHARGE';
}
function buildGroupedCharges(charges, selectedIds, groupingType, groupAll) {
    const byShipment = {};
    charges.forEach((charge) => {
        var _a, _b, _c, _d;
        const shipmentObjId = (_b = (_a = charge.shipment) === null || _a === void 0 ? void 0 : _a._id) !== null && _b !== void 0 ? _b : 'order-level';
        const displayId = (_d = (_c = charge.shipment) === null || _c === void 0 ? void 0 : _c.shipment_id) !== null && _d !== void 0 ? _d : 'Order Level';
        if (!byShipment[shipmentObjId]) {
            byShipment[shipmentObjId] = { charges: [], displayId };
        }
        byShipment[shipmentObjId].charges.push(charge);
    });
    return Object.entries(byShipment).map(([shipmentObjId, { charges: shipmentCharges, displayId }]) => {
        var _a;
        const template = cloneDeep((_a = BUCKET_TEMPLATES[groupingType]) !== null && _a !== void 0 ? _a : BUCKET_TEMPLATES.UNLIMITED);
        const chargeTypes = [];
        if (groupAll) {
            shipmentCharges.forEach((c) => {
                if (c.type && !chargeTypes.includes(c.type)) {
                    chargeTypes.push(c.type);
                }
            });
        }
        shipmentCharges.forEach((charge) => {
            const bucket = classifyCharge(charge, groupingType);
            if (template[bucket]) {
                template[bucket].charges.push({
                    charge,
                    selected: selectedIds.has(charge._id),
                });
                if (groupAll) {
                    template[bucket].groupedChargeTypes = chargeTypes;
                }
            }
        });
        return {
            shipmentId: shipmentObjId,
            shipmentDisplayId: displayId,
            grouping: template,
        };
    });
}
function buildPayload(groupedCharges) {
    return groupedCharges.map((shipmentData) => {
        const grouping = Object.entries(shipmentData.grouping).reduce((data, [key, bucket]) => {
            if (!bucket)
                return data;
            const selectedCharges = bucket.charges.filter((c) => c.selected);
            data[key] = {
                charges: selectedCharges.map((c) => { var _a; return ((_a = c.charge.shipment) === null || _a === void 0 ? void 0 : _a._id) ? c.charge.adjustment_id : c.charge._id; }),
                groupedChargeTypes: bucket.groupedChargeTypes,
            };
            return data;
        }, {});
        return {
            shipmentId: shipmentData.shipmentId,
            grouping,
        };
    });
}
function renameShipperCostToLineHaul(payload) {
    return payload.map((item) => {
        const _a = item.grouping, { SHIPPER_COST } = _a, remainder = __rest(_a, ["SHIPPER_COST"]);
        return Object.assign(Object.assign({}, item), { grouping: SHIPPER_COST
                ? Object.assign(Object.assign({}, remainder), { LINE_HAUL: SHIPPER_COST }) : remainder });
    });
}
function buildOrderLevelPayloadEntry(orderId, orderLevelCharges, selectedIds, groupAll, groupingType) {
    var _a;
    const selected = orderLevelCharges.filter((c) => selectedIds.has(c._id));
    if (selected.length === 0)
        return null;
    const templateKeys = Object.keys((_a = BUCKET_TEMPLATES[groupingType]) !== null && _a !== void 0 ? _a : BUCKET_TEMPLATES.UNLIMITED);
    const grouping = {};
    templateKeys.forEach((key) => {
        grouping[key] = { charges: [], groupedChargeTypes: [] };
    });
    const chargeTypes = [];
    if (groupAll) {
        selected.forEach((c) => {
            if (c.type && !chargeTypes.includes(c.type)) {
                chargeTypes.push(c.type);
            }
        });
    }
    selected.forEach((charge) => {
        const bucket = classifyCharge(charge, groupingType);
        if (grouping[bucket]) {
            grouping[bucket].charges.push(charge._id);
            if (groupAll) {
                grouping[bucket].groupedChargeTypes = chargeTypes;
            }
        }
    });
    // Rename SHIPPER_COST → LINE_HAUL to match API contract
    const { SHIPPER_COST } = grouping, remainder = __rest(grouping, ["SHIPPER_COST"]);
    return {
        orderId,
        grouping: SHIPPER_COST
            ? Object.assign(Object.assign({}, remainder), { LINE_HAUL: SHIPPER_COST }) : remainder,
    };
}
export function GenerateInvoiceDialog({ open, onClose, onSuccess, orderId, shipperId, charges, initialSelectedIds, groupLikeInvoiceItems, }) {
    var _a, _b, _c;
    const { toast } = useToast();
    const { data: invoiceConfig } = useShipperInvoiceConfig(shipperId);
    const invoiceGrouping = (_a = invoiceConfig === null || invoiceConfig === void 0 ? void 0 : invoiceConfig.invoiceGrouping) !== null && _a !== void 0 ? _a : 'UNLIMITED';
    const [groupedCharges, setGroupedCharges] = useState([]);
    const [groupAll, setGroupAll] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [areValidGroupings, setAreValidGroupings] = useState(false);
    const [invalidCharges, setInvalidCharges] = useState([]);
    const [showValidationAlert, setShowValidationAlert] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationResult, setGenerationResult] = useState(null);
    const [orderLevelSelected, setOrderLevelSelected] = useState(new Set());
    const validationSeq = useRef(0);
    const orderLevelSelectedRef = useRef(orderLevelSelected);
    orderLevelSelectedRef.current = orderLevelSelected;
    const groupedChargesRef = useRef(groupedCharges);
    groupedChargesRef.current = groupedCharges;
    // Only show charges that were selected in the parent table
    const selectedChargesOnly = useMemo(() => charges.filter((c) => initialSelectedIds.has(c._id)), [charges, initialSelectedIds]);
    const orderLevelCharges = useMemo(() => selectedChargesOnly.filter((c) => { var _a; return !((_a = c.shipment) === null || _a === void 0 ? void 0 : _a._id); }), [selectedChargesOnly]);
    const shipmentCharges = useMemo(() => selectedChargesOnly.filter((c) => { var _a; return !!((_a = c.shipment) === null || _a === void 0 ? void 0 : _a._id); }), [selectedChargesOnly]);
    useEffect(() => {
        if (open) {
            setGroupAll(groupLikeInvoiceItems !== null && groupLikeInvoiceItems !== void 0 ? groupLikeInvoiceItems : false);
            setShowValidationAlert(true);
            setGenerationResult(null);
            setInvalidCharges([]);
            setAreValidGroupings(false);
        }
    }, [open, groupLikeInvoiceItems]);
    useEffect(() => {
        if (!open)
            return;
        const orderLevelIds = new Set();
        orderLevelCharges.forEach((c) => {
            if (initialSelectedIds.has(c._id)) {
                orderLevelIds.add(c._id);
            }
        });
        setOrderLevelSelected(orderLevelIds);
    }, [open, orderLevelCharges, initialSelectedIds]);
    useEffect(() => {
        if (!open)
            return;
        const grouped = buildGroupedCharges(shipmentCharges, initialSelectedIds, invoiceGrouping, groupAll);
        setGroupedCharges(grouped);
    }, [open, shipmentCharges, initialSelectedIds, invoiceGrouping, groupAll]);
    // Stable serialized keys so the validation effect only re-runs when
    // the actual selection content changes, not on every new object reference.
    const validationKey = useMemo(() => {
        const shipmentSelections = groupedCharges.map((sg) => ({
            id: sg.shipmentId,
            selected: Object.entries(sg.grouping).flatMap(([, b]) => { var _a; return (_a = b === null || b === void 0 ? void 0 : b.charges.filter((c) => c.selected).map((c) => c.charge._id)) !== null && _a !== void 0 ? _a : []; }),
        }));
        const orderSelections = [...orderLevelSelected].sort();
        return JSON.stringify({ shipmentSelections, orderSelections });
    }, [groupedCharges, orderLevelSelected]);
    useEffect(() => {
        const currentGroupedCharges = groupedChargesRef.current;
        const currentOrderLevelSelected = orderLevelSelectedRef.current;
        if (!open ||
            !orderId ||
            (currentGroupedCharges.length === 0 && orderLevelCharges.length === 0))
            return;
        const requestId = ++validationSeq.current;
        const validate = async () => {
            var _a, _b;
            setIsValidating(true);
            try {
                const data = buildPayload(currentGroupedCharges);
                const selectedCharges = renameShipperCostToLineHaul(data);
                const orderLevelEntry = buildOrderLevelPayloadEntry(orderId, orderLevelCharges, currentOrderLevelSelected, groupAll, invoiceGrouping);
                const selectedOrderCharges = orderLevelEntry ? [orderLevelEntry] : [];
                const hasSelectedShipment = selectedCharges.some((sc) => Object.values(sc.grouping).some((b) => b && b.charges && b.charges.length > 0));
                const hasSelectedOrder = selectedOrderCharges.some((oc) => Object.values(oc.grouping).some((b) => b && b.charges && b.charges.length > 0));
                if (!hasSelectedShipment && !hasSelectedOrder) {
                    setIsValidating(false);
                    setAreValidGroupings(false);
                    return;
                }
                const response = await validateInvoiceChargesByGrouping(orderId, Object.assign({ selectedCharges }, (selectedOrderCharges.length > 0 && { selectedOrderCharges })));
                const results = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.results;
                if (requestId !== validationSeq.current)
                    return;
                if (results === null || results === void 0 ? void 0 : results.valid) {
                    setAreValidGroupings(true);
                    setInvalidCharges([]);
                }
                else {
                    setAreValidGroupings(false);
                    setInvalidCharges((_b = results === null || results === void 0 ? void 0 : results.invalidChargeGroupingsToInvoice) !== null && _b !== void 0 ? _b : []);
                }
            }
            catch (e) {
                console.error('[InvoiceDialog] validation error:', e);
                setAreValidGroupings(false);
            }
            finally {
                if (requestId === validationSeq.current)
                    setIsValidating(false);
            }
        };
        validate();
    }, [
        validationKey,
        orderId,
        open,
        orderLevelCharges,
        groupAll,
        invoiceGrouping,
    ]);
    // --- Handlers ---
    const handleToggleCharge = useCallback((shipmentId, bucketKey, chargeId, isGrouped, chargeType) => {
        setGroupedCharges((prev) => prev.map((sg) => {
            var _a;
            if (sg.shipmentId !== shipmentId)
                return sg;
            const bucket = sg.grouping[bucketKey];
            if (!bucket)
                return sg;
            const targetCharge = bucket.charges.find((c) => c.charge._id === chargeId);
            const newSelected = !((_a = targetCharge === null || targetCharge === void 0 ? void 0 : targetCharge.selected) !== null && _a !== void 0 ? _a : false);
            return Object.assign(Object.assign({}, sg), { grouping: Object.assign(Object.assign({}, sg.grouping), { [bucketKey]: Object.assign(Object.assign({}, bucket), { charges: bucket.charges.map((c) => {
                            if (isGrouped && c.charge.type === chargeType) {
                                return Object.assign(Object.assign({}, c), { selected: newSelected });
                            }
                            if (!isGrouped && c.charge._id === chargeId) {
                                return Object.assign(Object.assign({}, c), { selected: newSelected });
                            }
                            return c;
                        }) }) }) });
        }));
    }, []);
    const handleToggleBucketAll = useCallback((shipmentId, bucketKey, checked) => {
        setGroupedCharges((prev) => prev.map((sg) => {
            if (sg.shipmentId !== shipmentId)
                return sg;
            const bucket = sg.grouping[bucketKey];
            if (!bucket)
                return sg;
            return Object.assign(Object.assign({}, sg), { grouping: Object.assign(Object.assign({}, sg.grouping), { [bucketKey]: Object.assign(Object.assign({}, bucket), { charges: bucket.charges.map((c) => (Object.assign(Object.assign({}, c), { selected: checked }))) }) }) });
        }));
    }, []);
    const handleGroupChargeType = useCallback((shipmentId, bucketKey, chargeType) => {
        setGroupedCharges((prev) => prev.map((sg) => {
            if (sg.shipmentId !== shipmentId)
                return sg;
            const bucket = sg.grouping[bucketKey];
            if (!bucket)
                return sg;
            const currentGrouped = bucket.groupedChargeTypes;
            const newGrouped = currentGrouped.includes(chargeType)
                ? currentGrouped.filter((t) => t !== chargeType)
                : [...currentGrouped, chargeType];
            return Object.assign(Object.assign({}, sg), { grouping: Object.assign(Object.assign({}, sg.grouping), { [bucketKey]: Object.assign(Object.assign({}, bucket), { groupedChargeTypes: newGrouped }) }) });
        }));
    }, []);
    const handleToggleOrderLevelCharge = useCallback((chargeId) => {
        setOrderLevelSelected((prev) => {
            const next = new Set(prev);
            if (next.has(chargeId)) {
                next.delete(chargeId);
            }
            else {
                next.add(chargeId);
            }
            return next;
        });
    }, []);
    const handleToggleOrderLevelAll = useCallback((checked) => {
        setOrderLevelSelected(checked ? new Set(orderLevelCharges.map((c) => c._id)) : new Set());
    }, [orderLevelCharges]);
    const handleGenerate = useCallback(async (noDeliveryInvoice) => {
        var _a, _b, _c;
        if (!orderId)
            return;
        setIsGenerating(true);
        try {
            const data = buildPayload(groupedCharges);
            const selectedCharges = renameShipperCostToLineHaul(data);
            const orderLevelEntry = buildOrderLevelPayloadEntry(orderId, orderLevelCharges, orderLevelSelected, groupAll, invoiceGrouping);
            const selectedOrderCharges = orderLevelEntry ? [orderLevelEntry] : [];
            const payload = {
                ledgerDate: new Date().toISOString(),
                selectedCharges,
                useShipmentAddress: false,
                noDeliveryInvoice,
            };
            if (selectedOrderCharges.length > 0) {
                payload.selectedOrderCharges = selectedOrderCharges;
            }
            const response = await generateInvoicesByGrouping(orderId, payload);
            const results = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.results;
            const successes = (_b = results === null || results === void 0 ? void 0 : results.successes) !== null && _b !== void 0 ? _b : [];
            const errors = (_c = results === null || results === void 0 ? void 0 : results.errors) !== null && _c !== void 0 ? _c : [];
            if (!errors.length && successes.length) {
                toast({
                    title: 'Invoice Generated',
                    description: 'Invoice has been generated successfully.',
                });
                onSuccess();
                return;
            }
            if (errors.length && successes.length) {
                toast({
                    title: 'Partial Success',
                    description: 'Some invoices were generated but others failed.',
                });
                setGenerationResult(results);
                return;
            }
            toast({
                title: 'Invoice Generation Failed',
                description: 'Failed to generate invoices.',
                variant: 'destructive',
            });
            setGenerationResult(results);
        }
        catch (e) {
            console.error('Error generating invoice:', e);
            toast({
                title: 'Invoice Generation Failed',
                description: 'An error occurred while generating invoices.',
                variant: 'destructive',
            });
        }
        finally {
            setIsGenerating(false);
        }
    }, [
        orderId,
        groupedCharges,
        toast,
        onSuccess,
        orderLevelCharges,
        orderLevelSelected,
        groupAll,
        invoiceGrouping,
    ]);
    // --- Computed ---
    const grandTotal = useMemo(() => {
        let total = 0;
        groupedCharges.forEach((sg) => {
            Object.values(sg.grouping).forEach((bucket) => {
                if (!bucket)
                    return;
                bucket.charges.forEach((c) => {
                    var _a;
                    if (c.selected) {
                        total += (_a = c.charge.value) !== null && _a !== void 0 ? _a : 0;
                    }
                });
            });
        });
        orderLevelCharges.forEach((c) => {
            var _a;
            if (orderLevelSelected.has(c._id)) {
                total += (_a = c.value) !== null && _a !== void 0 ? _a : 0;
            }
        });
        return total;
    }, [groupedCharges, orderLevelCharges, orderLevelSelected]);
    const isGenerateDisabled = isGenerating || isValidating || !areValidGroupings;
    const isBillingManagerAdmin = SessionStore.isBillingManagerAdmin();
    return (React.createElement(Dialog, { open: open, onOpenChange: onClose },
        React.createElement(DialogContent, { className: "max-w-3xl max-h-[90vh] overflow-y-auto" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, { className: "text-xl font-semibold" }, "Generate Invoice")),
            React.createElement("div", { className: "space-y-4 mt-2" },
                React.createElement(CargomaticAlert, { type: "info", title: "Shipper Invoice Grouping Configuration" }, (_b = GROUPING_DISPLAY_NAMES[invoiceGrouping]) !== null && _b !== void 0 ? _b : invoiceGrouping),
                showValidationAlert && (React.createElement(React.Fragment, null,
                    isValidating && (React.createElement(CargomaticAlert, { type: "info", title: "Awaiting Charge Validation", canClose: true, onClose: () => setShowValidationAlert(false) },
                        React.createElement(Loader2, { className: "h-4 w-4 animate-spin" }))),
                    !isValidating && areValidGroupings && (React.createElement(CargomaticAlert, { type: "success", title: "Charges Valid For Invoicing", canClose: true, onClose: () => setShowValidationAlert(false) }, "All Selected Charges Valid For Invoicing")),
                    !isValidating && invalidCharges.length > 0 && (React.createElement(CargomaticAlert, { type: "error", title: "Some Selected Charges Invalid For Invoicing", canClose: true, onClose: () => setShowValidationAlert(false) },
                        React.createElement("div", null, "Some Selected Invoice Groupings Ineligible to Invoice"),
                        React.createElement("ul", { className: "ml-2 mt-2" }, invalidCharges.map((err, idx) => {
                            var _a;
                            return (React.createElement("li", { key: `invalid-${(_a = err.chargeType) === null || _a === void 0 ? void 0 : _a.toLowerCase()}-${idx}` }, err.chargeType));
                        })))))),
                generationResult && ((_c = generationResult.errors) === null || _c === void 0 ? void 0 : _c.length) > 0 && (React.createElement(CargomaticAlert, { type: "error", title: "Invoicing Response", canClose: true, onClose: () => setGenerationResult(null) },
                    React.createElement("div", null,
                        generationResult.successes.length,
                        " Successful",
                        ' ',
                        generationResult.successes.length !== 1
                            ? 'Invoices'
                            : 'Invoice'),
                    React.createElement("div", null,
                        generationResult.errors.length,
                        ' ',
                        generationResult.errors.length !== 1 ? 'Invoices' : 'Invoice',
                        ' ',
                        "Failed to Generate"),
                    React.createElement("ul", null, generationResult.errors.map((err, index) => {
                        var _a, _b;
                        const types = (_b = (_a = err.chargeTypes) === null || _a === void 0 ? void 0 : _a.map((t) => t.toLowerCase()).join(', ')) !== null && _b !== void 0 ? _b : '';
                        return (React.createElement("li", { key: `err-${types}-${index}` },
                            "Charge Type: ",
                            types));
                    })))),
                React.createElement("div", { className: "flex items-center justify-between py-2" },
                    React.createElement("span", { className: "text-base font-semibold" }, "Group Related Charges"),
                    React.createElement(Switch, { checked: groupAll, onCheckedChange: setGroupAll })),
                orderLevelCharges.length > 0 && (React.createElement("div", { className: "border rounded-lg" },
                    React.createElement("div", { className: "flex items-center justify-between px-4 py-3 border-b" },
                        React.createElement("div", { className: "flex items-center gap-3" },
                            React.createElement(Checkbox, { checked: orderLevelCharges.length > 0 &&
                                    orderLevelCharges.every((c) => orderLevelSelected.has(c._id)), onCheckedChange: (checked) => handleToggleOrderLevelAll(checked) }),
                            React.createElement("h3", { className: "font-bold text-lg" }, "Order Level")),
                        React.createElement("span", { className: "text-lg text-muted-foreground" }, formatMoney(orderLevelCharges
                            .filter((c) => orderLevelSelected.has(c._id))
                            .reduce((sum, c) => { var _a; return sum + ((_a = c.value) !== null && _a !== void 0 ? _a : 0); }, 0)))),
                    React.createElement("div", { className: "px-4 py-3 space-y-3" }, orderLevelCharges.map((charge) => {
                        var _a, _b, _c;
                        return (React.createElement("div", { key: charge._id, className: "flex items-center justify-between" },
                            React.createElement("div", { className: "flex items-center gap-3" },
                                React.createElement(Checkbox, { checked: orderLevelSelected.has(charge._id), onCheckedChange: () => handleToggleOrderLevelCharge(charge._id) }),
                                React.createElement("span", { className: "text-muted-foreground" }, charge.message),
                                ((_a = charge.equipment) === null || _a === void 0 ? void 0 : _a.equipment_id) ? (React.createElement(Badge, { variant: "secondary" }, charge.equipment.equipment_id)) : (!((_c = (_b = charge.invoice_id) === null || _b === void 0 ? void 0 : _b.number) === null || _c === void 0 ? void 0 : _c.startsWith('CM-')) && (React.createElement(MissingEquipmentWarning, null)))),
                            React.createElement("span", { className: "text-lg text-muted-foreground" }, formatMoney(charge.value))));
                    })))),
                groupedCharges.length > 0 && (React.createElement("div", { className: "border rounded-lg divide-y" }, groupedCharges.map((sg) => {
                    const hasCharges = Object.values(sg.grouping).some((b) => b && b.charges.length > 0);
                    if (!hasCharges)
                        return null;
                    return (React.createElement("div", { key: sg.shipmentId },
                        React.createElement("div", { className: "px-4 py-3" },
                            React.createElement("h3", { className: "font-bold text-lg" }, sg.shipmentDisplayId)),
                        React.createElement("div", { className: "px-4 pb-4" }, Object.entries(sg.grouping).map(([bucketKey, bucket]) => {
                            var _a;
                            if (!bucket || bucket.charges.length === 0)
                                return null;
                            return (React.createElement(BucketSection, { key: bucketKey, shipmentId: sg.shipmentId, bucketKey: bucketKey, bucketName: (_a = BUCKET_DISPLAY_NAMES[bucketKey]) !== null && _a !== void 0 ? _a : titleize(humanize(bucketKey)), bucket: bucket, groupAll: groupAll, onToggleCharge: handleToggleCharge, onToggleBucketAll: handleToggleBucketAll, onGroupChargeType: handleGroupChargeType }));
                        }))));
                })))),
            React.createElement(DialogFooter, { className: "flex items-center justify-between sm:justify-between mt-4" },
                React.createElement("div", { className: "text-lg font-semibold" },
                    "Grand Total: ",
                    formatMoney(grandTotal)),
                React.createElement("div", { className: "flex items-center gap-3" },
                    React.createElement(Button, { variant: "outline", onClick: onClose, disabled: isGenerating }, "Cancel"),
                    isBillingManagerAdmin ? (React.createElement(DropdownMenu, null,
                        React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Button, { variant: "outline", className: "border-zinc-300 text-zinc-900 hover:bg-zinc-100", disabled: isGenerateDisabled },
                                isGenerating && (React.createElement(Loader2, { className: "mr-2 h-4 w-4 animate-spin" })),
                                isGenerating ? 'Generating...' : 'Generate Invoice',
                                React.createElement(ChevronDown, { className: "ml-1 h-4 w-4" }))),
                        React.createElement(DropdownMenuContent, { align: "end" },
                            React.createElement(DropdownMenuItem, { onClick: () => handleGenerate(false) }, "Generate Invoice"),
                            React.createElement(DropdownMenuItem, { onClick: () => handleGenerate(true) }, "Generate No Delivery Invoice")))) : (React.createElement(Button, { variant: "outline", className: "border-zinc-300 text-zinc-900 hover:bg-zinc-100", disabled: isGenerateDisabled, onClick: () => handleGenerate(false) },
                        isGenerating && (React.createElement(Loader2, { className: "mr-2 h-4 w-4 animate-spin" })),
                        isGenerating ? 'Generating...' : 'Generate Invoice')))))));
}
function BucketSection({ shipmentId, bucketKey, bucketName, bucket, groupAll, onToggleCharge, onToggleBucketAll, onGroupChargeType, }) {
    const [isOpen, setIsOpen] = useState(true);
    const allBucketSelected = bucket.charges.length > 0 && bucket.charges.every((c) => c.selected);
    const bucketTotal = useMemo(() => {
        return bucket.charges.reduce((sum, c) => { var _a; return sum + ((_a = c.charge.value) !== null && _a !== void 0 ? _a : 0); }, 0);
    }, [bucket.charges]);
    const typeCountsInBucket = useMemo(() => {
        const counts = new Map();
        bucket.charges.forEach((c) => {
            var _a, _b;
            const t = (_a = c.charge.type) !== null && _a !== void 0 ? _a : '';
            counts.set(t, ((_b = counts.get(t)) !== null && _b !== void 0 ? _b : 0) + 1);
        });
        return counts;
    }, [bucket.charges]);
    const displayRows = useMemo(() => {
        const { groupedChargeTypes } = bucket;
        if (!groupedChargeTypes.length) {
            return bucket.charges.map((c) => {
                var _a;
                return ({
                    key: c.charge._id,
                    charge: c,
                    isGrouped: false,
                    groupedValue: (_a = c.charge.value) !== null && _a !== void 0 ? _a : 0,
                    groupItems: [c],
                });
            });
        }
        const rows = [];
        const grouped = groupBy(bucket.charges, (c) => { var _a; return (_a = c.charge.type) !== null && _a !== void 0 ? _a : ''; });
        Object.entries(grouped).forEach(([type, charges]) => {
            if (!groupedChargeTypes.includes(type)) {
                charges.forEach((c) => {
                    var _a;
                    rows.push({
                        key: c.charge._id,
                        charge: c,
                        isGrouped: false,
                        groupedValue: (_a = c.charge.value) !== null && _a !== void 0 ? _a : 0,
                        groupItems: [c],
                    });
                });
            }
            else {
                const total = charges.reduce((sum, c) => { var _a; return sum + ((_a = c.charge.value) !== null && _a !== void 0 ? _a : 0); }, 0);
                rows.push({
                    key: charges[0].charge._id,
                    charge: charges[0],
                    isGrouped: true,
                    groupedValue: total,
                    groupItems: charges,
                });
            }
        });
        return rows;
    }, [bucket]);
    return (React.createElement("div", { className: "mb-4" },
        React.createElement("button", { className: "w-full flex items-center justify-between py-2 hover:bg-muted/50 transition-colors", onClick: () => setIsOpen(!isOpen) },
            React.createElement("span", { className: "font-medium" }, bucketName),
            React.createElement(ChevronDown, { className: `h-5 w-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}` })),
        isOpen && (React.createElement("div", { className: "mt-2" },
            React.createElement("div", { className: "bg-muted/30 rounded-lg overflow-hidden" },
                React.createElement(Table, null,
                    React.createElement(TableHeader, null,
                        React.createElement(TableRow, { className: "bg-muted/50" },
                            React.createElement(TableHead, { className: "w-12" },
                                React.createElement(Checkbox, { checked: allBucketSelected, onCheckedChange: (checked) => onToggleBucketAll(shipmentId, bucketKey, checked) })),
                            React.createElement(TableHead, null, "Name"),
                            React.createElement(TableHead, null, "Amount"),
                            React.createElement(TableHead, null, "Group Related Charges?"))),
                    React.createElement(TableBody, null,
                        displayRows.map((row) => {
                            var _a, _b, _c;
                            const { charge: entry, groupedValue, key, groupItems } = row;
                            const chargeType = (_a = entry.charge.type) !== null && _a !== void 0 ? _a : '';
                            const isGroupable = ((_b = typeCountsInBucket.get(chargeType)) !== null && _b !== void 0 ? _b : 0) > 1;
                            const isTypeGrouped = bucket.groupedChargeTypes.includes(chargeType);
                            return (React.createElement(TableRow, { key: key },
                                React.createElement(TableCell, null,
                                    React.createElement(Checkbox, { checked: entry.selected, onCheckedChange: () => onToggleCharge(shipmentId, bucketKey, entry.charge._id, isTypeGrouped, chargeType) })),
                                React.createElement(TableCell, null,
                                    React.createElement("div", { className: "flex items-center gap-2" },
                                        React.createElement("span", null, titleize(humanize(chargeType)) ||
                                            entry.charge.message),
                                        groupItems.some((item) => {
                                            var _a, _b, _c;
                                            return !((_a = item.charge.equipment) === null || _a === void 0 ? void 0 : _a.equipment_id) &&
                                                !((_c = (_b = item.charge.invoice_id) === null || _b === void 0 ? void 0 : _b.number) === null || _c === void 0 ? void 0 : _c.startsWith('CM-'));
                                        }) ? (React.createElement(MissingEquipmentWarning, null)) : (React.createElement(Badge, { variant: "secondary" }, (_c = entry.charge.equipment) === null || _c === void 0 ? void 0 : _c.equipment_id)))),
                                React.createElement(TableCell, null, formatMoney(groupedValue)),
                                React.createElement(TableCell, null, isGroupable && !groupAll && (React.createElement(Switch, { checked: isTypeGrouped, onCheckedChange: () => onGroupChargeType(shipmentId, bucketKey, chargeType) })))));
                        }),
                        React.createElement(TableRow, { className: "bg-muted/50 font-semibold" },
                            React.createElement(TableCell, { colSpan: 2 }, "Total"),
                            React.createElement(TableCell, null, formatMoney(bucketTotal)),
                            React.createElement(TableCell, null)))))))));
}
export default GenerateInvoiceDialog;
